import React, { useState, useEffect, useRef, memo } from "react";
import axios from "axios";
import swal from "sweetalert";
import "../styles/dashboard_styles.css";
import MuuriWidget from "./MuuriWidget";
import Navbar from "./Navbar";
import AddDashboardModal from "./AddDashboardModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrashAlt,
  faPlus,
  faDownload,
  faUpload,
  faCog,
  faEdit,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import config from "../config.js";
import Cookie from "universal-cookie";
import { decrypt } from "./useCryptoJS";
import mixpanel from "mixpanel-browser";
import _ from "lodash";
import { DataProvider } from "./DataContext"; // Import DataProvider for batch data fetching

// Memoize static components to prevent unnecessary re-renders
const MemoizedNavbar = memo(Navbar);
const MemoizedMuuriWidget = memo(MuuriWidget);

// Axios Interceptor
axios.interceptors.request.use(
  (config) => {
    const cookies = new Cookie();
    const userToken = cookies.get("userToken");
    if (userToken) config.headers.Authorization = `Bearer ${userToken}`;
    return config;
  },
  (error) => Promise.reject(error)
);

const Dashboard = () => {
  const cookies = new Cookie();
  const [editModal, setEditModal] = useState(false);
  const [paneSize, setPaneSize] = useState(300);
  const [paneArray, setPaneArray] = useState([]);
  const [toggleAddDashboard, setToggleAddDashboard] = useState(false);
  const [newDashboard, setNewDashboard] = useState("");
  const [dashboardData, setDashboardData] = useState([]);
  const [selectedDashboards, setSelectedDashboards] = useState([]);
  const [isPendingDashboardData, setIsPendingDashboardData] = useState(true);
  const [isNewDashboard, setIsNewDashboard] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const muuriWidgetRef = useRef(null);
  const fileUploadRef = useRef(null);
  const previousSelectedRef = useRef(selectedDashboards);

  const [isTopPanelOpen, setIsTopPanelOpen] = useState(false); // New state for top panel

  // States for rename functionality
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameInput, setRenameInput] = useState("");
  const [renameError, setRenameError] = useState("");

  let actionInitiatingUser = -1;
  let urdashboard = "0000";
  let userToken = null;
  try {
    actionInitiatingUser = parseInt(cookies.get("userID"), 10);
    urdashboard = decrypt(cookies.get("urdashboard")) || "0000";
    userToken = cookies.get("userToken");
  } catch (error) {
    console.error("Error parsing user data:", error);
  }

  useEffect(() => {
    previousSelectedRef.current = selectedDashboards;
  }, [selectedDashboards]);

  useEffect(() => {
    if (!userToken || actionInitiatingUser === -1) {
      swal("Invalid Session", "Please log in again.", "error").then(() => {
        window.location.replace(config.FRONTEND_URL);
      });
      return;
    }

    const fetchDashboards = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          config.BACKEND_URL + "/dashboards/load-all"
        );
        const options = response.data.dashboards.map((dashboard) => ({
          value: dashboard.dashboard_name,
          label: dashboard.dashboard_name,
        }));
        setDashboardData(options);
      } catch (error) {
        if (error.response?.status === 401) {
          swal("Session Expired", "Please log in again.", "error").then(() => {
            window.location.replace(config.FRONTEND_URL);
          });
        } else {
          console.error("Error fetching dashboards:", error.message);
          swal("Error", "Failed to load dashboards.", "error");
        }
      } finally {
        setIsLoading(false);
        setIsPendingDashboardData(false);
      }
    };

    fetchDashboards();
  }, [actionInitiatingUser, userToken]);

  const toggleEditModal = () => setEditModal((prev) => !prev);
  const toggleTopPanel = () => {
    setIsTopPanelOpen((prev) => !prev);
  };
  const unloadDashboard = () => {
    setNewDashboard("");
    setPaneArray([]);
    setSelectedDashboards([]);
    previousSelectedRef.current = [];
    if (muuriWidgetRef.current?.updateLayouts)
      muuriWidgetRef.current.updateLayouts([]);
  };

  const handleSelectChange = (value, actionMeta) => {
    if (actionMeta.action === "clear" && newDashboard) {
      swal({
        title: "Unload Dashboard?",
        text: "Do you want to unload the current dashboard?",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then((willUnload) => {
        if (willUnload) unloadDashboard();
        else
          setSelectedDashboards(
            previousSelectedRef.current.length > 0
              ? previousSelectedRef.current
              : []
          );
      });
    } else {
      const newSelected = value || [];
      if (
        newDashboard &&
        !newSelected.some((option) => option.value === newDashboard)
      ) {
        newSelected.push({ value: newDashboard, label: newDashboard });
      }
      setSelectedDashboards(newSelected);
    }
  };

  const isOptionDisabled = (option) =>
    newDashboard && option.value === newDashboard;

  const saveDashboard = _.debounce(async () => {
    if (!newDashboard || !newDashboard.trim()) {
      swal("Dashboard name cannot be empty!", "", "error");
      return;
    }
    const dashboard = {
      dashboard_name: newDashboard.trim(),
      dashboard_panes: paneArray || [],
    };
    swal({
      title: "Save Dashboard?",
      text: "Do you want to save the dashboard?",
      icon: "warning",
      buttons: true,
    }).then(async (willSave) => {
      if (willSave) {
        setIsLoading(true);
        try {
          const response = await axios.post(
            config.BACKEND_URL + "/dashboards/save",
            { dashboard, actionInitiatingUser, isNewDashboard }
          );
          if (isNewDashboard) {
            setDashboardData((prev) => [
              ...prev,
              { value: newDashboard, label: newDashboard },
            ]);
            setIsNewDashboard(false);
          }
          swal("Dashboard saved!", "", "success");
          mixpanel.track("Save Dashboard", { userID: actionInitiatingUser });
        } catch (err) {
          swal(
            "Error saving dashboard!",
            err.response?.data?.message || "An error occurred",
            "error"
          );
        } finally {
          setIsLoading(false);
        }
      }
    });
  }, 300);

  const addDashboard = () => {
    setToggleAddDashboard(true);
    mixpanel.track("Add Dashboard", { userID: actionInitiatingUser });
  };

  const loadDashboard = _.debounce(async () => {
    if (selectedDashboards.length !== 1) {
      swal("Select one dashboard to load!", "", "error");
      return;
    }
    const dashName = selectedDashboards[0].value;
    swal({
      title: "Load Dashboard?",
      text: "Do you want to load this dashboard?",
      icon: "warning",
      buttons: true,
    }).then(async (willLoad) => {
      if (willLoad) {
        setIsLoading(true);
        try {
          const response = await axios.post(
            config.BACKEND_URL + "/dashboards/content",
            { dash_name: dashName, actionInitiatingUser }
          );
          setNewDashboard(response.data.dashboard_name);
          setPaneArray(response.data.dashboard_panes);
          setIsNewDashboard(false);
          setSelectedDashboards([
            {
              value: response.data.dashboard_name,
              label: response.data.dashboard_name,
            },
          ]);
          mixpanel.track("Load Dashboard", { userID: actionInitiatingUser });
        } catch (error) {
          swal(
            "Error loading dashboard!",
            error.response?.data?.message || "An error occurred",
            "error"
          );
        } finally {
          setIsLoading(false);
        }
      }
    });
  }, 300);

  const deleteDashboard = async () => {
    if (selectedDashboards.length === 0) {
      swal("No dashboard selected!", "", "error");
      return;
    }
    const selected = selectedDashboards.map((option) => option.value);
    swal({
      title: "Delete Dashboards?",
      text: "This action cannot be undone!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        setIsLoading(true);
        try {
          const response = await axios.post(
            config.BACKEND_URL + "/dashboards/delete-by-names",
            { names: selected, actionInitiatingUser }
          );
          setDashboardData((prev) =>
            prev.filter((d) => !selected.includes(d.value))
          );
          if (selected.includes(newDashboard)) unloadDashboard();
          else
            setSelectedDashboards((prev) =>
              prev.filter((d) => !selected.includes(d.value))
            );
          swal("Dashboards deleted!", "", "success");
          mixpanel.track("Delete Dashboard", { userID: actionInitiatingUser });
        } catch (err) {
          swal(
            "Error deleting dashboards!",
            err.response?.data?.message || "An error occurred",
            "error"
          );
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const addDashboardHandler = (dashboardName) => {
    if (
      !dashboardName ||
      dashboardName.trim() === "" ||
      dashboardData.some((d) => d.value === dashboardName)
    ) {
      swal(
        dashboardName
          ? "Dashboard name already exists!"
          : "Dashboard name cannot be empty!",
        "",
        "error"
      );
      return;
    }
    setNewDashboard(dashboardName.trim());
    setPaneArray([]);
    setIsNewDashboard(true);
  };

  const addPane = () => {
    if (!newDashboard) {
      swal("Please load a dashboard first!", "", "warning");
      return;
    }
    const maxPaneId = Math.max(
      ...paneArray.map((pane) => pane.paneId || -1),
      -1
    );
    const newPane = {
      x: 0,
      y: 0,
      w: 4,
      h: 9,
      minWidth: 3,
      maxWidth: 12,
      minHeight: 5,
      widgetsArray: [],
      paneId: maxPaneId + 1,
    };
    setPaneArray((prev) => {
      const updated = [...prev, newPane];
      if (muuriWidgetRef.current?.updateLayouts)
        muuriWidgetRef.current.updateLayouts(updated);
      return updated;
    });
  };

  const downloadBoard = () => {
    if (selectedDashboards.length === 0) {
      swal("Select at least one dashboard!", "", "error");
      return;
    }
    const selected = selectedDashboards.map((d) => d.value);
    const isAll = selected.length === dashboardData.length;
    const url = isAll
      ? `${config.BACKEND_URL}/dashboards/download-all`
      : selected.length === 1
      ? `${
          config.BACKEND_URL
        }/dashboards/download?dashName=${encodeURIComponent(selected[0])}`
      : `${config.BACKEND_URL}/dashboards/download-multiple`;
    setIsLoading(true);
    axios
      .request({
        url,
        method: isAll || selected.length === 1 ? "GET" : "POST",
        data:
          isAll || selected.length === 1 ? null : { dashboardNames: selected },
        responseType: "blob",
        headers: {
          "Content-Type": isAll ? "application/zip" : "application/json",
        },
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          isAll || selected.length > 1
            ? "Dashboards.zip"
            : `${selected[0]}.json`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        mixpanel.track("Download Dashboard", { userID: actionInitiatingUser });
      })
      .catch(() => {
        swal("Error downloading dashboards!", "", "error");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const uploadDashboard = (event) => {
    event.preventDefault();
    fileUploadRef.current.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (
      !file ||
      !["json", "zip"].includes(file.name.toLowerCase().split(".").pop())
    ) {
      swal(
        file ? "Only JSON or ZIP files supported!" : "No file selected!",
        "",
        "error"
      );
      if (fileUploadRef.current) fileUploadRef.current.value = "";
      return;
    }
    setIsLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("actionInitiatingUser", actionInitiatingUser);
    try {
      const response = await axios.post(
        `${config.BACKEND_URL}/dashboards/upload${
          file.name.endsWith(".json") ? "-single" : ""
        }`,
        file.name.endsWith(".json")
          ? { dashboard: JSON.parse(await file.text()), actionInitiatingUser }
          : formData,
        {
          headers: file.name.endsWith(".zip")
            ? { "Content-Type": "multipart/form-data" }
            : {},
        }
      );
      const uploadedDashboards =
        response.data.results
          ?.filter((r) => r.action === "created" || r.action === "updated")
          .map((r) => ({
            value: r.name || r.dashboard_name,
            label: r.name || r.dashboard_name,
          })) || [];
      setDashboardData((prev) => [...prev, ...uploadedDashboards]);
      swal(
        "Upload Successful!",
        response.data.message || "Dashboards uploaded!",
        "success"
      );
    } catch (error) {
      swal(
        "Upload Failed!",
        error.response?.data?.message || "An error occurred",
        "error"
      );
    } finally {
      if (fileUploadRef.current) fileUploadRef.current.value = "";
      setIsLoading(false);
    }
  };

  const handleRenameClick = () => {
    if (selectedDashboards.length !== 1) {
      swal("Please select exactly one dashboard to rename!", "", "error");
      return;
    }
    setRenameInput(selectedDashboards[0].value);
    setRenameError("");
    setShowRenameModal(true);
  };

  const confirmRename = async () => {
    if (!renameInput || renameInput.trim() === "") {
      setRenameError("New name cannot be empty!");
      return;
    }
    if (renameInput === selectedDashboards[0].value) {
      setRenameError("New name must be different from the old name!");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(
        config.BACKEND_URL + "/dashboards/rename",
        {
          oldName: selectedDashboards[0].value,
          newName: renameInput.trim(),
          actionInitiatingUser,
        }
      );
      if (response.status === 200) {
        setDashboardData((prev) =>
          prev.map((d) =>
            d.value === selectedDashboards[0].value
              ? { value: renameInput.trim(), label: renameInput.trim() }
              : d
          )
        );
        setSelectedDashboards([
          { value: renameInput.trim(), label: renameInput.trim() },
        ]);
        if (newDashboard === selectedDashboards[0].value)
          setNewDashboard(renameInput.trim());
        setShowRenameModal(false);
        swal("Dashboard renamed successfully!", "", "success");
      }
    } catch (error) {
      setRenameError(
        error.response?.data?.message || "Error renaming dashboard"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const customStyles = {
    container: (provided) => ({ ...provided, width: "100%" }),
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#1f2937",
      border: state.isFocused ? "1px solid #3b82f6" : "1px solid #4a5568",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.3)" : "none",
      "&:hover": { borderColor: "#3b82f6" },
      // zIndex: 1002, // Ensure control is above top panel
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#1f2937",
      zIndex: 1002,
      position: "absolute",
      marginTop: "4px",
    }),
    option: (provided, { isSelected, isFocused }) => ({
      ...provided,
      backgroundColor: isSelected
        ? "#3b82f6"
        : isFocused
        ? "#2d3748"
        : "#1f2937",
      color: "#fff",
      "&:hover": { backgroundColor: "#2d3748" },
    }),
    multiValue: (provided) => ({ ...provided, backgroundColor: "#3b82f6" }),
    multiValueLabel: (provided) => ({ ...provided, color: "#fff" }),
    multiValueRemove: (provided, { data }) => ({
      ...provided,
      color: "#fff",
      display:
        newDashboard && data.value === newDashboard ? "none" : "inline-flex",
      "&:hover": { backgroundColor: "#2d3748", color: "#fff" },
    }),
    placeholder: (provided) => ({ ...provided, color: "#a0aec0" }),
    input: (provided) => ({ ...provided, color: "#fff" }),
    singleValue: (provided) => ({ ...provided, color: "#fff" }),
  };

  return (
    <div className="Dashboard">
      {toggleAddDashboard && (
        <AddDashboardModal
          setToggleAddDashboard={setToggleAddDashboard}
          addDashboardHandler={addDashboardHandler}
          newDashboard={newDashboard}
        />
      )}
      {editModal && (
        <div className="modalOverlay">
          <div className="overlay" onClick={toggleEditModal}></div>
          <div className="modal-content">
            <form>
              <div className="form-row">
                <label>Pane Size</label>
                <input
                  name="pane_size"
                  type="number"
                  defaultValue={paneSize}
                  onChange={(e) =>
                    setPaneSize(parseInt(e.target.value, 10) || 300)
                  }
                />
              </div>
              <button
                className="btn-primary"
                onClick={(e) => {
                  e.preventDefault();
                  setPaneSize(
                    parseInt(
                      new FormData(e.target.form).get("pane_size"),
                      10
                    ) || 300
                  );
                  setEditModal(false);
                }}
              >
                Submit
              </button>
            </form>
          </div>
        </div>
      )}
      {showRenameModal && (
        <div className="modalOverlay">
          <div
            className="overlay"
            onClick={() => setShowRenameModal(false)}
          ></div>
          <div className="modal-content">
            <h2>Rename Dashboard</h2>
            <div className="form-row">
              <label>Current Name: {selectedDashboards[0].value}</label>
            </div>
            <div className="form-row">
              <label htmlFor="newDashboardName">New Name:</label>
              <input
                type="text"
                id="newDashboardName"
                value={renameInput}
                onChange={(e) => setRenameInput(e.target.value)}
              />
            </div>
            {renameError && <div className="error-message">{renameError}</div>}
            <div className="modal-actions">
              <button
                className="btn-primary"
                onClick={confirmRename}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Renaming...
                  </>
                ) : (
                  "Rename"
                )}
              </button>
              <button
                className="btn-cancel"
                onClick={() => setShowRenameModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <input
        type="file"
        ref={fileUploadRef}
        style={{ display: "none" }}
        onChange={handleFileUpload}
        accept=".json,.zip"
      />
      {/* <MemoizedNavbar newDashboard={newDashboard} onToggleTopPanel={toggleTopPanel} />
      <div className="top-panel">
        <div className="dashboard-header">
          <div className="dashboard-actions">
            <div className="search-and-primary-actions">
              <div className="dashboard-select-container">
                {isPendingDashboardData ? (
                  <div className="text-gray-400">Loading...</div>
                ) : dashboardData.length === 0 ? (
                  <div className="text-gray-400">No dashboards available!</div>
                ) : (
                  <Select
                    isMulti
                    isClearable
                    options={dashboardData}
                    value={selectedDashboards}
                    onChange={handleSelectChange}
                    isOptionDisabled={isOptionDisabled}
                    placeholder="Select Dashboard"
                    styles={customStyles}
                    className="dashboard-select"
                  />
                )}
              </div>
              {(urdashboard.charAt(0) === "1" || urdashboard.charAt(1) === "1" || urdashboard.charAt(3) === "1") && (
                <div className="primary-action-buttons">
                  <button
                    className="action-button primary"
                    onClick={loadDashboard}
                    disabled={selectedDashboards.length !== 1 || isLoading}
                  >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faUpload} />} Load
                  </button>
                  <button
                    className="action-button primary"
                    onClick={saveDashboard}
                    disabled={!newDashboard || isLoading}
                  >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faDownload} />} Save
                  </button>
                </div>
              )}
            </div>
            {(urdashboard.charAt(0) === "1" || urdashboard.charAt(1) === "1" || urdashboard.charAt(3) === "1") && (
              <div className="secondary-action-buttons">
                <div className="button-group">
                  <button className="action-button primary" onClick={addDashboard} disabled={isLoading}>
                    <FontAwesomeIcon icon={faPlus} /> New
                  </button>
                  <button
                    className="action-button primary"
                    onClick={downloadBoard}
                    disabled={selectedDashboards.length === 0 || isLoading}
                  >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faDownload} />} Save As
                  </button>
                  <button className="action-button primary" onClick={uploadDashboard} disabled={isLoading}>
                    <FontAwesomeIcon icon={faUpload} /> Open
                  </button>
                  <button
                    className="action-button danger"
                    onClick={deleteDashboard}
                    disabled={selectedDashboards.length === 0 || isLoading}
                  >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTrashAlt} />} Delete
                  </button>
                  <button
                    className="action-button primary"
                    onClick={handleRenameClick}
                    disabled={selectedDashboards.length !== 1 || isLoading}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Rename
                  </button>
                  <button className="action-button primary" onClick={addPane} disabled={!newDashboard || isLoading}>
                    <FontAwesomeIcon icon={faPlus} /> Add Pane
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {isLoading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}
      </div> */}

      <MemoizedNavbar
        newDashboard={newDashboard}
        onToggleTopPanel={toggleTopPanel}
      />
      {/* <div className={`top-panel ${isTopPanelOpen ? 'expanded' : 'collapsed'}`}>
        {isTopPanelOpen && (
          <div className="dashboard-header">
            <div className="dashboard-actions">
              <div className="search-and-primary-actions">
                <div className="dashboard-select-container">
                  {urdashboard === "0000" ? (   // ✅ no dashboard permissions at all
                    <div className="text-red-400 font-semibold">No access to dashboard</div>
                  ) :
                    isPendingDashboardData ? (
                      <div className="text-gray-400">Loading...</div>
                    ) : dashboardData.length === 0 ? (
                      <div className="text-gray-400">No dashboards available!</div>
                    ) : (
                      <Select
                        isMulti
                        isClearable
                        options={dashboardData}
                        value={selectedDashboards}
                        onChange={handleSelectChange}
                        isOptionDisabled={isOptionDisabled}
                        placeholder="Select Dashboard"
                        styles={customStyles}
                        className="dashboard-select"
                      />
                    )}
                </div>
                {(urdashboard.charAt(0) === "1" || urdashboard.charAt(1) === "1" || urdashboard.charAt(3) === "1") && (
                  <div className="primary-action-buttons">
                    <button
                      className="action-button primary"
                      onClick={loadDashboard}
                      disabled={selectedDashboards.length !== 1 || isLoading}
                    >
                      {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faUpload} />} Load
                    </button>
                    <button
                      className="action-button primary"
                      onClick={saveDashboard}
                      disabled={!newDashboard || isLoading}
                    >
                      {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faDownload} />} Save
                    </button>
                  </div>
                )}
              </div>
              {(urdashboard.charAt(0) === "1" || urdashboard.charAt(1) === "1" || urdashboard.charAt(3) === "1") && (
                <div className="secondary-action-buttons">
                  <div className="button-group">
                    <button className="action-button primary" onClick={addDashboard} disabled={isLoading}>
                      <FontAwesomeIcon icon={faPlus} /> New
                    </button>
                    <button
                      className="action-button primary"
                      onClick={downloadBoard}
                      disabled={selectedDashboards.length === 0 || isLoading}
                    >
                      {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faDownload} />} Save As
                    </button>
                    <button className="action-button primary" onClick={uploadDashboard} disabled={isLoading}>
                      <FontAwesomeIcon icon={faUpload} /> Open
                    </button>
                    <button
                      className="action-button danger"
                      onClick={deleteDashboard}
                      disabled={selectedDashboards.length === 0 || isLoading}
                    >
                      {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTrashAlt} />} Delete
                    </button>
                    <button
                      className="action-button primary"
                      onClick={handleRenameClick}
                      disabled={selectedDashboards.length !== 1 || isLoading}
                    >
                      <FontAwesomeIcon icon={faEdit} /> Rename
                    </button>
                    <button className="action-button primary" onClick={addPane} disabled={!newDashboard || isLoading}>
                      <FontAwesomeIcon icon={faPlus} /> Add Pane
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {isLoading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}
      </div> */}

      <div className={`top-panel ${isTopPanelOpen ? "expanded" : "collapsed"}`}>
        {isTopPanelOpen && (
          <div className="dashboard-header">
            <div className="dashboard-actions">
              <div className="search-and-primary-actions">
                <div className="dashboard-select-container">
                  {urdashboard === "0000" ? ( // ✅ no dashboard permissions at all
                    <div className="text-red-400 font-semibold">
                      No access to dashboard
                    </div>
                  ) : isPendingDashboardData ? (
                    <div className="text-gray-400">Loading...</div>
                  ) : dashboardData.length === 0 ? (
                    <div className="text-gray-400">
                      No dashboards available!
                    </div>
                  ) : (
                    <Select
                      isMulti
                      isClearable
                      options={dashboardData}
                      value={selectedDashboards}
                      onChange={handleSelectChange}
                      isOptionDisabled={isOptionDisabled}
                      placeholder="Select Dashboard"
                      styles={customStyles}
                      className="dashboard-select"
                    />
                  )}
                </div>

                {/* ================= PERMISSIONS ================= */}
                {(() => {
                  const canView = urdashboard.charAt(0) === "1";
                  const canEdit = urdashboard.charAt(1) === "1";
                  const canCreate = urdashboard.charAt(2) === "1";
                  const canDelete = urdashboard.charAt(3) === "1";

                  return (
                    <div className="primary-action-buttons">
                      {/* View → Load */}
                      {canView && (
                        <button
                          className="action-button primary"
                          onClick={loadDashboard}
                          disabled={
                            selectedDashboards.length !== 1 || isLoading
                          }
                        >
                          {isLoading ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <FontAwesomeIcon icon={faUpload} />
                          )}{" "}
                          Load
                        </button>
                      )}

                      {/* Edit → Save */}
                      {canEdit && (
                        <button
                          className="action-button primary"
                          onClick={saveDashboard}
                          disabled={!newDashboard || isLoading}
                        >
                          {isLoading ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <FontAwesomeIcon icon={faDownload} />
                          )}{" "}
                          Save
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* ================= SECONDARY ACTIONS ================= */}
              {(() => {
                const canView = urdashboard.charAt(0) === "1";
                const canEdit = urdashboard.charAt(1) === "1";
                const canCreate = urdashboard.charAt(2) === "1";
                const canDelete = urdashboard.charAt(3) === "1";

                return (
                  <div className="secondary-action-buttons">
                    <div className="button-group">
                      {/* Create → New */}
                      {canCreate && (
                        <button
                          className="action-button primary"
                          onClick={addDashboard}
                          disabled={isLoading}
                        >
                          <FontAwesomeIcon icon={faPlus} /> New
                        </button>
                      )}

                      {/* Edit → Save As */}
                      {canEdit && (
                        <button
                          className="action-button primary"
                          onClick={downloadBoard}
                          disabled={
                            selectedDashboards.length === 0 || isLoading
                          }
                        >
                          {isLoading ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <FontAwesomeIcon icon={faDownload} />
                          )}{" "}
                          Save As
                        </button>
                      )}

                      {/* Edit → Open */}
                      {canEdit && (
                        <button
                          className="action-button primary"
                          onClick={uploadDashboard}
                          disabled={isLoading}
                        >
                          <FontAwesomeIcon icon={faUpload} /> Open
                        </button>
                      )}

                      {/* Delete → Delete */}
                      {canDelete && (
                        <button
                          className="action-button danger"
                          onClick={deleteDashboard}
                          disabled={
                            selectedDashboards.length === 0 || isLoading
                          }
                        >
                          {isLoading ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <FontAwesomeIcon icon={faTrashAlt} />
                          )}{" "}
                          Delete
                        </button>
                      )}

                      {/* Edit → Rename */}
                      {canEdit && (
                        <button
                          className="action-button primary"
                          onClick={handleRenameClick}
                          disabled={
                            selectedDashboards.length !== 1 || isLoading
                          }
                        >
                          <FontAwesomeIcon icon={faEdit} /> Rename
                        </button>
                      )}

                      {/* Create → Add Pane */}
                      {canCreate && (
                        <button
                          className="action-button primary"
                          onClick={addPane}
                          disabled={!newDashboard || isLoading}
                        >
                          <FontAwesomeIcon icon={faPlus} /> Add Pane
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {isLoading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}
      </div>

      {/* Wrap MemoizedMuuriWidget with DataProvider to provide batched MQTT data to all widgets */}
      <DataProvider paneArray={paneArray}>
        <MemoizedMuuriWidget
          newDashboard={newDashboard}
          paneWidth={paneSize}
          paneArray={paneArray}
          setPaneArray={setPaneArray}
          addPane={addPane}
          ref={muuriWidgetRef}
        />
      </DataProvider>
    </div>
  );
};

export default Dashboard;
